import React from "react"
import { Box, capitalize, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew, shades } from "@palette"


// Tones for the figure tiles, by name
const TILE_TONES = {
	green: shades.green[800],
	blue: dsColorNew.webBlue500,
	orange: shades.deepOrange[700],
	purple: shades.deepPurple[600],
	red: dsColorNew.dangerRed,
}

// A small figure card: tinted icon, label and value
export function AppStatTile({ icon, label, value, tone = 'blue', valueColor }) {
	const color = TILE_TONES[tone] || tone

	return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.5, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '12px', minWidth: 0 }}>
		<Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(color, 0.1), color }}>
			<IconSeeker icon={icon} sx={{ fontSize: 14 }}/>
		</Box>
		<Box sx={{ minWidth: 0 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
			<Typography component="div" sx={{ fontSize: 20, fontWeight: 700, color: valueColor || dsColorNew.textTitle, lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 0.5 }}>{value}</Typography>
		</Box>
	</Box>
}

// A bordered group of details with a small title
export function AppDetailSection({ icon, title, children, sx = {} }) {
	return <Box sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '12px', px: 2, py: 1.75, ...sx }}>
		<Typography component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, mb: 1.5 }}>
			{!!icon && <IconSeeker icon={icon} sx={{ fontSize: 13, color: dsColorNew.appBlue }}/>}
			{title}
		</Typography>
		{children}
	</Box>
}

// Label / value pairs; items without a value are left out: [{ label, value }]
export function AppDetailList({ items = [], labelWidth = 150 }) {
	const shown = items.filter(({ value }) => value !== undefined && value !== null && value !== false && value !== '')

	return <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: `${labelWidth}px 1fr`, rowGap: 1.5, columnGap: 1.5, alignItems: 'start', m: 0 }}>
		{shown.map(({ label, value }) => <React.Fragment key={label}>
			<Typography component="dt" sx={{ fontSize: 13, color: dsColorNew.textMuted, pt: '3px' }}>{label}</Typography>
			<Box component="dd" sx={{ m: 0, fontSize: 14, color: dsColorNew.textTitle, display: 'flex', flexWrap: 'wrap', gap: 0.6, minWidth: 0 }}>{value}</Box>
		</React.Fragment>)}
	</Box>
}

const STATUS_TONES = {
	ready: { bg: dsColorNew.successBg, color: dsColorNew.successText, icon: 'check' },
	finished: { bg: dsColorNew.successBg, color: dsColorNew.successText, icon: 'check' },
	running: { bg: dsColorNew.noteInfoBg, color: dsColorNew.noteInfo, icon: 'spinner' },
	stopped: { bg: dsColorNew.dangerBg, color: dsColorNew.dangerRed, icon: 'hand' },
	error: { bg: dsColorNew.dangerBg, color: dsColorNew.dangerRed, icon: 'triangle-exclamation' },
	// News announcements; expired and retracted stay grey with their own text
	live: { bg: dsColorNew.successBg, color: dsColorNew.successText, icon: 'circle-play' },
	scheduled: { bg: dsColorNew.noteInfoBg, color: dsColorNew.noteInfo, icon: 'clock' },
}

// An execution status as a small pill; unknown statuses show in grey with their own text
export function AppStatusPill({ status }) {
	const tone = STATUS_TONES[String(status || '').toLowerCase()] || { bg: dsColorNew.pillTrackBg, color: dsColorNew.textDark }

	return <Box component="span" sx={{
		display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.1, py: 0.4, borderRadius: '999px',
		backgroundColor: tone.bg, color: tone.color, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
	}}>
		{!!tone.icon && <IconSeeker icon={tone.icon} sx={{ fontSize: 11 }}/>}
		{capitalize(String(status || ''))}
	</Box>
}

// Yes / no in a table cell: a green tick or a grey dash
export function AppCheckMark({ value, label }) {
	return value
		? <IconSeeker icon="check" aria-label={label ? `${label}: yes` : 'yes'} sx={{ color: dsColorNew.successText, fontSize: 15 }}/>
		: <Typography component="span" aria-label={label ? `${label}: no` : 'no'} sx={{ color: dsColorNew.textSubtle, fontSize: 15 }}>—</Typography>
}
